import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { PanelsTopLeft, Users2, Download, ExternalLink, Trash2, Pencil, Upload, Sparkles, X, Plus, CalendarCheck2, Package, Rocket, Check, ShieldCheck } from "lucide-react";
import { CtaButton, MonoLabel, SecondaryButton } from "../components/kit";
import { ProductLinkImport } from "../components/ProductLinkImport";
import api, { formatApiErrorDetail } from "../lib/api";

const OBJECTIVES = [
  { key: "lead", name: "Lead Capture", desc: "Services: get contact requests.", icon: Users2 },
  { key: "booking", name: "Booking", desc: "Local business: get booking requests.", icon: CalendarCheck2 },
  { key: "offer", name: "Offer / Product", desc: "One product: drive to checkout.", icon: Package },
  { key: "waitlist", name: "Waitlist / Launch", desc: "Coming soon: collect emails.", icon: Rocket },
];
const GOAL_OBJECTIVE = { leads: "lead", sales: "offer", bookings: "booking", awareness: "waitlist" };
const DEFAULT_FIELDS = [
  { key: "name", label: "Name", type: "text", required: true },
  { key: "email", label: "Email", type: "email", required: true },
  { key: "phone", label: "Phone", type: "phone", required: false },
];
const slugKey = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 40) || "q";

const Field = ({ label, children, full }) => (
  <div className={full ? "sm:col-span-2" : ""}>
    <label className="text-[11px] font-semibold text-[#9A9591] block mb-1.5">{label}</label>
    {children}
  </div>
);

const StepHeader = ({ n, title, note }) => (
  <div className="flex items-center gap-3">
    <span className="w-7 h-7 rounded-full bg-[#181621] text-white text-[12.5px] font-extrabold flex items-center justify-center tabular-nums shrink-0">{n}</span>
    <p className="font-bold text-[14.5px] text-[#181621]">{title}</p>
    {note && <span className="text-[11.5px] text-[#9A9591]">{note}</span>}
  </div>
);

export default function LandingPages() {
  const [tab, setTab] = useState("landings");
  const [landings, setLandings] = useState(undefined);
  const [leads, setLeads] = useState([]);
  const [wizard, setWizard] = useState(false);
  const [objective, setObjective] = useState("lead");
  const [brief, setBrief] = useState({ offer: "", offer_details: "", benefits: "", social_proof: "", desired_action: "form", contact_value: "", tone: "", faq_points: "", product_url: "" });
  const [fields, setFields] = useState(DEFAULT_FIELDS);
  const [newQ, setNewQ] = useState({ label: "", type: "text", options: "", required: false });
  const [images, setImages] = useState([]);
  const [building, setBuilding] = useState(false);
  const [readingUrl, setReadingUrl] = useState(false);
  const [usage, setUsage] = useState(null);
  const loadUsage = () => api.get("/creative-studio/usage").then(({ data }) => setUsage(data)).catch(() => {});
  const [stage, setStage] = useState("");
  const pollRef = useRef(null);
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const load = () => api.get("/landing").then(({ data }) => setLandings(data)).catch(() => setLandings([]));
  useEffect(() => {
    load();
    loadUsage();
    api.get("/leads").then(({ data }) => setLeads(data)).catch(() => {});
    api.get("/brand-brain").then(({ data }) => {
      setObjective(GOAL_OBJECTIVE[data?.primary_goal] || "lead");
      const moods = data?.brand_kit?.mood_keywords || [];
      if (moods.length) setBrief((b) => ({ ...b, tone: moods.join(", ") }));
      if (params.get("build") === "1") setWizard(true);
    }).catch(() => {});
    api.get("/landing/jobs/status").then(({ data }) => {
      if (data.status === "pending" && data.kind === "generate") { setBuilding(true); setWizard(true); startPoll(); }
    }).catch(() => {});
    return () => pollRef.current && clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startPoll = () => {
    pollRef.current && clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get("/landing/jobs/status");
        if (data.status === "complete" && data.landing_id) {
          clearInterval(pollRef.current);
          toast("Your page is ready.", { description: "Review it, refine it, publish it." });
          navigate(`/app/performance/landing/${data.landing_id}`);
        } else if (data.status === "failed") {
          clearInterval(pollRef.current); setBuilding(false);
          toast.error(data.error || "The build failed — try again.");
        }
      } catch { /* keep */ }
    }, 5000);
  };

  const generate = async () => {
    setBuilding(true);
    setStage("Briefing the designer…");
    try {
      await api.post("/landing/generate-v2", {
        objective,
        brief: { ...brief, benefits: brief.benefits.split("\n").map((x) => x.trim()).filter(Boolean).slice(0, 5) },
        form_schema: fields,
        image_urls: images,
      });
      setStage("Hand-coding your page — 1-2 minutes…");
      startPoll();
    } catch (err) {
      setBuilding(false);
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const applyProduct = (p) => setBrief((b) => ({
    ...b,
    offer: b.offer || p.name || "",
    offer_details: b.offer_details || [p.price_text, p.description].filter(Boolean).join(" — ").slice(0, 400),
    benefits: b.benefits || (p.benefits || []).join("\n"),
    product_url: b.product_url || p.url || "",
  }));

  const readProductUrl = async () => {
    const url = brief.product_url.trim();
    if (!url || readingUrl) return;
    setReadingUrl(true);
    try {
      const { data } = await api.post("/product-link/extract", { url });
      if (data.status !== "ok" || !data.product) throw new Error("failed");
      applyProduct(data.product);
      if (!images.length && (data.product.images || [])[0]) {
        try {
          const { data: img } = await api.post("/product-link/import-image", { image_url: data.product.images[0], target: "landing" });
          if (img.url) setImages((im) => (im.includes(img.url) ? im : [...im, img.url]));
        } catch { /* image is optional */ }
      }
      toast("Page read — offer, details and benefits pre-filled. Edit anything.");
    } catch {
      toast.error("I couldn't read that page — add the details or a photo manually.");
    } finally {
      setReadingUrl(false);
    }
  };

  const uploadImg = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/landing/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImages((im) => [...im, data.url]);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const aiScene = async () => {
    setStage("ai");
    try {
      const { data } = await api.post("/landing/ai-scene");
      setImages((im) => [...im, data.url]);
      loadUsage();
      toast("AI scene added.", { description: "This used ⅓ of a creative set from your monthly budget." });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setStage("");
    }
  };

  const addQuestion = () => {
    const label = newQ.label.trim();
    if (label.length < 2) return;
    const f = { key: slugKey(label), label, type: newQ.type, required: newQ.required };
    if (newQ.type === "select") f.options = newQ.options.split(",").map((x) => x.trim()).filter(Boolean);
    if (fields.some((x) => x.key === f.key)) { toast.error("A question with this name exists."); return; }
    setFields([...fields, f]);
    setNewQ({ label: "", type: "text", options: "", required: false });
  };

  const togglePublish = async (l) => {
    try {
      if (l.status === "published") await api.post(`/landing/${l.id}/unpublish`);
      else {
        const { data } = await api.post(`/landing/${l.id}/publish`);
        toast("Published.", { description: `/l/${data.slug} is live.` });
      }
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (l) => {
    if (!window.confirm(`Delete "${l.title}"? Its leads stay in your Leads list.`)) return;
    await api.delete(`/landing/${l.id}`);
    load();
  };

  const exportCsv = () => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/leads/export`, "_blank");
  const publishedCount = (landings || []).filter((l) => l.status === "published").length;
  const canGenerate = brief.offer.trim().length >= 2 && (objective !== "offer" || brief.product_url.trim()) && (!["call", "whatsapp"].includes(brief.desired_action) || brief.contact_value.trim());
  const setsLeft = usage ? Math.round((usage.limit - usage.used) * 10) / 10 : null;

  return (
    <div className="p-6 sm:p-10 max-w-5xl" data-testid="landing-pages-page">
      <MonoLabel>Performance Studio · Landing Pages</MonoLabel>
      <h1 className="mt-5 text-3xl sm:text-4xl leading-tight font-extrabold tracking-[-0.02em]">
        Bespoke landing pages, hand-coded by AI for your business
      </h1>

      <div className="inline-flex rounded-xl border border-[#EAE5DB] bg-white overflow-hidden mt-6">
        {[["landings", "Landings", PanelsTopLeft], ["leads", `Leads (${leads.length})`, Users2]].map(([key, label, Icon], i) => (
          <button key={key} onClick={() => setTab(key)} className={`inline-flex items-center gap-1.5 px-4 py-2 text-[13px] font-bold transition-colors ${i > 0 ? "border-l border-[#EAE5DB]" : ""} ${tab === key ? "bg-[#181621] text-white" : "bg-white text-[#54505B] hover:bg-[#FBFAF7]"}`} data-testid={`landing-tab-${key}`}>
            <Icon size={13} strokeWidth={2} /> {label}
          </button>
        ))}
      </div>

      {tab === "landings" && (
        <>
          <div className="flex items-end justify-between mt-6 flex-wrap gap-4">
            <div className="min-w-[180px]" data-testid="landing-published-counter">
              <div className="h-1.5 rounded-full bg-[#F3EFE7] overflow-hidden">
                <div className="h-full rounded-full transition-[width] duration-500" style={{ width: `${(publishedCount / 3) * 100}%`, background: "linear-gradient(90deg, #E0603A, #0B67FF)" }} />
              </div>
              <p className="text-[11.5px] font-semibold text-[#54505B] mt-1.5 tabular-nums">{publishedCount} of 3 published</p>
            </div>
            <CtaButton className="!py-2.5 !px-5 text-[13.5px]" onClick={() => setWizard(!wizard)} data-testid="landing-new-btn">+ New landing</CtaButton>
          </div>

          {wizard && building && (
            <div className="card-soft mt-5 p-10 text-center" data-testid="landing-building-state">
              <div className="icon-tile !w-12 !h-12 mx-auto animate-pulse" style={{ background: "var(--blue-bg)" }}>
                <Sparkles size={20} strokeWidth={1.8} style={{ color: "var(--blue-fg)" }} />
              </div>
              <p className="font-bold text-[15px] text-[#181621] mt-4">{stage || "Hand-coding your page — 1-2 minutes…"}</p>
              <p className="text-[12.5px] text-[#54505B] mt-1.5">The AI is writing a bespoke page from your snapshot, brand kit and brief.</p>
            </div>
          )}

          {wizard && !building && (
            <div className="space-y-5 mt-5" data-testid="landing-wizard">
              <div className="card-soft p-6 sm:p-7">
                <StepHeader n={1} title="The goal" />
                <div className="grid sm:grid-cols-4 gap-3 mt-4">
                  {OBJECTIVES.map((t) => {
                    const sel = objective === t.key;
                    return (
                      <button key={t.key} onClick={() => setObjective(t.key)} className={`rounded-xl border p-3.5 text-left transition-colors ${sel ? "border-[#181621] bg-white shadow-sm" : "border-[#EAE5DB] bg-white/60 hover:border-[#9A9591]"}`} data-testid={`landing-objective-${t.key}`}>
                        <span className={`w-7 h-7 rounded-lg flex items-center justify-center ${sel ? "bg-[#181621]" : "bg-[#F3EFE7]"}`}>
                          <t.icon size={14} strokeWidth={1.8} className={sel ? "text-white" : "text-[#54505B]"} />
                        </span>
                        <span className="font-extrabold text-[13.5px] text-[#181621] block mt-2">{t.name}</span>
                        <span className="text-[11.5px] text-[#54505B] mt-0.5 block leading-snug">{t.desc}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="card-soft p-6 sm:p-7">
                <StepHeader n={2} title="The brief" note="the more real details, the better the page" />
                <div className="mt-4 rounded-xl border border-[#EAE5DB] bg-[#FBFAF7] p-4">
                  <ProductLinkImport
                    target="landing"
                    multi
                    onApply={applyProduct}
                    onImage={(data) => setImages((im) => (im.includes(data.url) ? im : [...im, data.url]))}
                  />
                </div>
                <div className="grid sm:grid-cols-2 gap-4 mt-4">
                  <Field label="The offer — required" full>
                    <input className="field-input" placeholder='e.g. "Free first consultation" or "-20% launch week"' value={brief.offer} onChange={(e) => setBrief({ ...brief, offer: e.target.value })} data-testid="brief-offer-input" />
                  </Field>
                  <Field label="Real details: price, deadline, conditions" full>
                    <textarea className="field-input min-h-[60px]" placeholder='e.g. "€490 instead of €700, only until June 30, first 20 clients"' value={brief.offer_details} onChange={(e) => setBrief({ ...brief, offer_details: e.target.value })} data-testid="brief-details-input" />
                  </Field>
                  <Field label="3-5 concrete benefits, in your words (one per line)" full>
                    <textarea className="field-input min-h-[80px]" placeholder={"We answer within 2 hours\nNo win, no fee\n12 years in the region"} value={brief.benefits} onChange={(e) => setBrief({ ...brief, benefits: e.target.value })} data-testid="brief-benefits-input" />
                  </Field>
                  <Field label="Social proof (testimonials, numbers, certifications) — optional">
                    <textarea className="field-input min-h-[60px]" placeholder='"200+ clients funded" · "Maria R.: they got us €80k"' value={brief.social_proof} onChange={(e) => setBrief({ ...brief, social_proof: e.target.value })} data-testid="brief-proof-input" />
                  </Field>
                  <Field label="FAQ points / objections — optional">
                    <textarea className="field-input min-h-[60px]" placeholder="How much does it cost? How long does it take?" value={brief.faq_points} onChange={(e) => setBrief({ ...brief, faq_points: e.target.value })} data-testid="brief-faq-input" />
                  </Field>
                  <Field label="Desired action">
                    <select className="field-input" value={brief.desired_action} onChange={(e) => setBrief({ ...brief, desired_action: e.target.value })} data-testid="brief-action-select">
                      <option value="form">Fill the form</option>
                      <option value="call">Call now</option>
                      <option value="whatsapp">WhatsApp message</option>
                      <option value="booking">Booking request</option>
                    </select>
                  </Field>
                  <Field label="Tone (from your Brand Kit — edit freely)">
                    <input className="field-input" placeholder="e.g. premium, warm, direct" value={brief.tone} onChange={(e) => setBrief({ ...brief, tone: e.target.value })} data-testid="brief-tone-input" />
                  </Field>
                  {["call", "whatsapp"].includes(brief.desired_action) && (
                    <Field label={brief.desired_action === "call" ? "Phone number" : "WhatsApp number (international, no +)"}>
                      <input className="field-input" placeholder={brief.desired_action === "call" ? "+39 02 1234567" : "393331234567"} value={brief.contact_value} onChange={(e) => setBrief({ ...brief, contact_value: e.target.value })} data-testid="brief-contact-input" />
                    </Field>
                  )}
                  {objective === "offer" && (
                    <Field label="Checkout / product URL (the CTA links here — we read it to pre-fill the brief)">
                      <div className="flex gap-2">
                        <input className="field-input flex-1" placeholder="https://…" value={brief.product_url} onChange={(e) => setBrief({ ...brief, product_url: e.target.value })} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); readProductUrl(); } }} data-testid="brief-url-input" />
                        <SecondaryButton className="!py-2 !px-3.5 !text-[12.5px] shrink-0" onClick={readProductUrl} disabled={readingUrl || !brief.product_url.trim()} data-testid="brief-url-read-btn">
                          {readingUrl ? "Reading…" : "Read page"}
                        </SecondaryButton>
                      </div>
                    </Field>
                  )}
                </div>
              </div>

              <div className="card-soft p-6 sm:p-7">
                <StepHeader n={3} title="Images" note="your photos first · never fake people" />
                <div className="flex flex-wrap items-center gap-3 mt-4">
                  {images.map((u, i) => (
                    <span key={u} className="relative">
                      <img src={`${process.env.REACT_APP_BACKEND_URL}${u}`} alt="" className="h-16 w-16 object-cover rounded-lg border border-[#EAE5DB]" data-testid={`brief-img-${i}`} />
                      <button onClick={() => setImages(images.filter((x) => x !== u))} className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-[#181621] text-white flex items-center justify-center" data-testid={`brief-img-remove-${i}`}><X size={10} /></button>
                    </span>
                  ))}
                  <label className="h-16 w-24 rounded-xl border border-[#EAE5DB] bg-[#FBFAF7] hover:border-[#181621] transition-colors flex flex-col items-center justify-center gap-1 text-[#54505B] cursor-pointer shadow-sm">
                    <Upload size={14} strokeWidth={1.8} />
                    <span className="text-[10.5px] font-bold">Upload</span>
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadImg(e.target.files?.[0])} data-testid="brief-img-upload" />
                  </label>
                  <SecondaryButton className="!py-2 !px-3.5 !text-[12px] flex items-center gap-1.5" onClick={aiScene} disabled={stage === "ai"} data-testid="brief-ai-scene-btn">
                    <Sparkles size={12} /> {stage === "ai" ? "Shooting…" : "AI scene · ⅓ set"}
                  </SecondaryButton>
                  {usage && (
                    <div className="min-w-[150px]" data-testid="landing-usage-counter">
                      <div className="h-1.5 rounded-full bg-[#F3EFE7] overflow-hidden">
                        <div className="h-full rounded-full transition-[width] duration-500" style={{ width: `${usage.limit ? Math.min(100, (usage.used / usage.limit) * 100) : 0}%`, background: setsLeft < 2 ? "var(--amber-fg)" : "linear-gradient(90deg, #E0603A, #0B67FF)" }} />
                      </div>
                      <p className={`text-[10.5px] font-semibold mt-1 tabular-nums ${setsLeft < 2 ? "text-[#96691E]" : "text-[#54505B]"}`}>{setsLeft} of {usage.limit} creative sets left</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="card-soft p-6 sm:p-7">
                <StepHeader n={4} title="The form" />
                <div className="space-y-2 mt-4" data-testid="form-builder">
                  {fields.map((f, i) => (
                    <div key={f.key} className="flex items-center gap-3 rounded-xl border border-[#EAE5DB] bg-white px-3.5 py-2.5 shadow-sm" data-testid={`form-field-${f.key}`}>
                      <input className="flex-1 text-[13px] font-semibold outline-none bg-transparent" value={f.label} onChange={(e) => setFields(fields.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} data-testid={`form-field-label-${f.key}`} />
                      <span className="inline-flex items-center rounded-full border border-[#EAE5DB] bg-[#FBFAF7] px-2.5 py-0.5 text-[10px] font-bold text-[#54505B] uppercase tracking-[0.06em]">{f.type}</span>
                      <label className="flex items-center gap-1.5 text-[11px] font-bold text-[#54505B] cursor-pointer">
                        <input type="checkbox" checked={!!f.required} onChange={(e) => setFields(fields.map((x, j) => (j === i ? { ...x, required: e.target.checked } : x)))} data-testid={`form-field-required-${f.key}`} /> required
                      </label>
                      {!["name", "email"].includes(f.key) && (
                        <button onClick={() => setFields(fields.filter((_, j) => j !== i))} className="text-[#9A9591] hover:text-[#B4552D]" data-testid={`form-field-remove-${f.key}`}><Trash2 size={13} /></button>
                      )}
                    </div>
                  ))}
                  <div className="flex flex-wrap items-center gap-2 pt-1">
                    <input className="field-input !py-2 !text-[12.5px] w-[200px]" placeholder="New question…" value={newQ.label} onChange={(e) => setNewQ({ ...newQ, label: e.target.value })} data-testid="form-new-label" />
                    <select className="field-input !py-2 !text-[12.5px] w-[110px]" value={newQ.type} onChange={(e) => setNewQ({ ...newQ, type: e.target.value })} data-testid="form-new-type">
                      <option value="text">Text</option>
                      <option value="select">Select</option>
                      <option value="yesno">Yes/No</option>
                    </select>
                    {newQ.type === "select" && <input className="field-input !py-2 !text-[12.5px] w-[180px]" placeholder="Options, comma-separated" value={newQ.options} onChange={(e) => setNewQ({ ...newQ, options: e.target.value })} data-testid="form-new-options" />}
                    <label className="flex items-center gap-1.5 text-[11px] font-bold text-[#54505B] cursor-pointer">
                      <input type="checkbox" checked={newQ.required} onChange={(e) => setNewQ({ ...newQ, required: e.target.checked })} data-testid="form-new-required" /> required
                    </label>
                    <SecondaryButton className="!py-2 !px-3 !text-[12px]" onClick={addQuestion} data-testid="form-new-add"><Plus size={12} /> Add</SecondaryButton>
                  </div>
                  <p className="text-[11px] text-[#9A9591] flex items-center gap-1.5 pt-1">
                    <ShieldCheck size={12} strokeWidth={1.8} className="text-[#3E7B3E]" /> GDPR consent checkbox is always included on the published page.
                  </p>
                </div>

                <CtaButton className="mt-6" onClick={generate} disabled={!canGenerate} data-testid="landing-generate-btn">
                  <Sparkles size={14} /> Build my page
                </CtaButton>
              </div>
            </div>
          )}

          <div className="space-y-3 mt-6">
            {landings === undefined && <div className="card-soft h-24 animate-pulse" />}
            {(landings || []).map((l) => (
              <div key={l.id} className="card-soft p-5 flex items-center gap-4 flex-wrap" data-testid={`landing-row-${l.id}`}>
                <div className="flex-1 min-w-[200px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-extrabold text-[14.5px] text-[#181621]">{l.title}</p>
                    {l.status === "published" ? (
                      <span className="inline-flex items-center gap-1 rounded-full border border-[#BFDDB8] bg-white px-2.5 py-0.5 text-[10.5px] font-bold text-[#3E7B3E] uppercase tracking-[0.05em]"><Check size={10} strokeWidth={2.5} /> Published</span>
                    ) : (
                      <span className="inline-flex items-center rounded-full border border-[#EAE5DB] bg-[#FBFAF7] px-2.5 py-0.5 text-[10.5px] font-bold text-[#54505B] uppercase tracking-[0.05em]">{l.status}</span>
                    )}
                    <span className="inline-flex items-center rounded-full border border-[#D9D2EE] bg-white px-2.5 py-0.5 text-[10.5px] font-bold text-[#6A5AA8] uppercase tracking-[0.05em]">{l.engine === "v2" ? "Bespoke" : (l.template || "")}</span>
                  </div>
                  <p className="text-[12px] text-[#9A9591] mt-1 tabular-nums">/l/{l.slug} · {l.leads_count} leads</p>
                </div>
                {l.status === "published" && (
                  <a href={`/l/${l.slug}`} target="_blank" rel="noreferrer" className="text-[12.5px] font-bold text-[#54505B] hover:text-[#181621] flex items-center gap-1" data-testid={`landing-view-${l.id}`}>View <ExternalLink size={12} /></a>
                )}
                <SecondaryButton className="!py-1.5 !px-3 !text-[12.5px]" onClick={() => navigate(`/app/performance/landing/${l.id}`)} data-testid={`landing-edit-${l.id}`}><Pencil size={12} /> Edit</SecondaryButton>
                <SecondaryButton className="!py-1.5 !px-3 !text-[12.5px]" onClick={() => togglePublish(l)} data-testid={`landing-publish-toggle-${l.id}`}>{l.status === "published" ? "Unpublish" : "Publish"}</SecondaryButton>
                <button onClick={() => remove(l)} className="text-[#9A9591] hover:text-[#B4552D]" data-testid={`landing-delete-${l.id}`}><Trash2 size={15} strokeWidth={1.8} /></button>
              </div>
            ))}
            {landings && !landings.length && !wizard && (
              <div className="card-soft p-10 text-center" data-testid="landing-empty">
                <div className="icon-tile !w-12 !h-12 mx-auto" style={{ background: "var(--neutral-bg)" }}>
                  <PanelsTopLeft size={20} strokeWidth={1.8} style={{ color: "var(--neutral-fg)" }} />
                </div>
                <p className="font-bold text-[15px] text-[#181621] mt-4">No landings yet — your first one takes 2 minutes.</p>
              </div>
            )}
          </div>
        </>
      )}

      {tab === "leads" && (
        <div className="mt-6" data-testid="leads-tab">
          <div className="flex justify-end">
            <SecondaryButton className="!py-2 !px-4 !text-[13px] flex items-center gap-1.5" onClick={exportCsv} disabled={!leads.length} data-testid="leads-export-btn"><Download size={13} /> Export CSV</SecondaryButton>
          </div>
          <div className="card-soft mt-3 overflow-x-auto">
            {leads.length ? (
              <table className="w-full text-[13px]">
                <thead><tr className="text-left border-b border-[#F3EFE7]">{["Date", "Name", "Contact", "Answers", "Landing"].map((h) => <th key={h} className="px-5 py-3 text-[10px] font-bold tracking-[0.1em] text-[#9A9591] uppercase">{h}</th>)}</tr></thead>
                <tbody>
                  {leads.map((ld) => (
                    <tr key={ld.id} className="border-b border-[#F3EFE7] last:border-0 hover:bg-[#FBFAF7] transition-colors" data-testid={`lead-row-${ld.id}`}>
                      <td className="px-5 py-3 text-[#54505B] tabular-nums">{(ld.created_at || "").slice(0, 10)}</td>
                      <td className="px-5 py-3 font-bold">{ld.name || "—"}</td>
                      <td className="px-5 py-3">{ld.email || ld.phone || "—"}</td>
                      <td className="px-5 py-3 text-[#54505B]">{[ld.datetime_pref, ...Object.entries(ld.custom || {}).map(([k, v]) => `${k}: ${v}`)].filter(Boolean).join(" · ") || "—"}</td>
                      <td className="px-5 py-3 text-[#54505B]">{ld.landing_title || ld.landing_slug}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="p-8 text-center text-[13px] text-[#54505B]" data-testid="leads-empty">No leads yet — publish a landing and share it.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
